// src/contexts/AuthContext.js
//
// Single source of truth for authentication context. Extracted verbatim from
// the inline AuthProvider that previously lived in App.js, so behavior is
// unchanged for existing callers. The only addition is a derived `isCustomer`
// boolean used by dashboard / hierarchy / reports / OLAP / intelligence
// components to redact vendor/cost/profit/fake fields from the UI.
//
// `login(userData, token, sessionId)` is a post-login setter — the actual
// POST to /auth/login lives in Login.js, which calls this via onLogin.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiCall } from '../Api';
import {
  getCurrentToken,
  saveSessionToken,
  removeCurrentSession,
} from '../SessionManager';

const CONTEXT_PATH = process.env.REACT_APP_CONTEXT_PATH || '/cdrf';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Detect embed mode — skip all auth redirects
  const isEmbedRoute = window.location.pathname.includes('/embed');

  const handleUnauthorized = () => {
    if (isEmbedRoute) return; // Never redirect in embed mode
    removeCurrentSession();
    setUser(null);
    window.location.href = `${CONTEXT_PATH}/login`;
  };

  const validateToken = async (token) => {
    try {
      const response = await apiCall('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else {
        // Handle 401/403 - session expired or unauthorized
        if (response.status === 401 || response.status === 403) {
          handleUnauthorized();
        } else {
          removeCurrentSession();
          setUser(null);
        }
        return null;
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      removeCurrentSession();
      setUser(null);
      return null;
    }
  };

  const login = (userData, token, sessionId) => {
    saveSessionToken(sessionId, token);
    // Set what the caller gave us so the UI flips out of the login page
    // immediately, then refetch /me to get the canonical user shape (the
    // /auth/login response has customer_id at the top level, not folded
    // into `user` — without this, `isCustomer` is false until the 60s
    // refresh tick lands).
    setUser(userData);
    validateToken(token);
  };

  const logout = async () => {
    try {
      const token = getCurrentToken();
      if (token) {
        await apiCall('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      removeCurrentSession();
      setUser(null);
    }
  };

  const checkAuth = async () => {
    if (isEmbedRoute) { setLoading(false); return; }
    const token = getCurrentToken();
    if (token) {
      await validateToken(token);
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Periodic token validation — skip in embed mode
  useEffect(() => {
    if (!user || isEmbedRoute) return;

    const interval = setInterval(async () => {
      const token = getCurrentToken();
      if (token) {
        const userData = await validateToken(token);
        if (!userData) {
          return;
        }
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [user]);

  // Global error handler for 401/403 responses — skip in embed mode
  useEffect(() => {
    if (isEmbedRoute) return;

    const handleGlobalError = (event) => {
      if (event.detail && (event.detail.status === 401 || event.detail.status === 403)) {
        handleUnauthorized();
      }
    };

    window.addEventListener('unauthorized', handleGlobalError);
    return () => window.removeEventListener('unauthorized', handleGlobalError);
  }, []);

  // Derived: true iff the user is bound to a single customer_id (host_customer
  // role from SMSC). Mirrors the backend's customer_redaction.is_customer().
  const cid = user?.customer_id;
  const cidStr = cid != null ? String(cid) : '';
  const isCustomer = cid != null && cid !== -1 && cidStr !== '-1' && cidStr !== '';

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      isAuthenticated: !!user,
      isCustomer,
      validateToken,
      handleUnauthorized
    }}>
      {children}
    </AuthContext.Provider>
  );
};
